/**
 * AdSystemIcon — знак рекламной системы для бейджа в шапке отчёта.
 *
 * В дизайн-системе таких знаков пока нет: `Icon` работает с Material
 * Symbols, а это фирменная марка со своими градиентами, которые ни к
 * какому токену не сводятся и меняться вместе с темой не должны. Поэтому
 * иконка живёт на странице и вынесена в список дефицита — если систем
 * станет больше одной, набор переезжает в `atoms`.
 *
 * Геометрия перенесена из макета без пересчёта: в квадрате 24 круг занимает
 * 20.7 — те самые 1.65 отступа, которые макет и рисует. Стрелка обрезается
 * по кругу, как в исходной маске; сам круг лежит в системе координат
 * исходного контура, поэтому пути и градиенты переносятся дословно.
 *
 * @format
 */

import {useId} from 'react';

export interface AdSystemIconProps {
  /** Сторона квадрата в пикселях. По умолчанию — кегль иконки при `label.m`. */
  size?: number;
  /** Подпись для скринридера. Пусто — знак декоративный. */
  title?: string;
}

/**
 * Сдвиг исходного контура так, чтобы круг встал в центр квадрата 24×24.
 * Центр круга в координатах контура — (22.35, 10.3277).
 */
const OFFSET_X = 12 - 22.35;
const OFFSET_Y = 12 - 10.3277;

/** Круглая подложка знака. */
const DISC = 'M32.7006 0H12.0006V20.6554H32.7006V0Z';

/** Стрелка. Путь целиком из макета, включая правило заполнения. */
const ARROW =
  'M35.4261 1.24439C35.7725 1.35447 36.0934 1.53234 36.3701 1.7676C36.6468 2.00286 36.8737 2.29079 37.0377 2.61457C37.2016 2.93835 37.2992 3.29148 37.3248 3.65332C37.3504 4.01517 37.3034 4.37848 37.1867 4.72202L31.9184 20.368C31.4296 21.8196 29.849 22.6163 28.3876 22.1475C28.0412 22.0373 27.7203 21.8594 27.4437 21.6241C27.167 21.3888 26.9401 21.1008 26.7763 20.777C26.6124 20.4532 26.5148 20.1001 26.4893 19.7383C26.4637 19.3764 26.5107 19.0131 26.6274 18.6696L28.3901 13.4344L3.89736 35.8638L0 31.6261L24.476 9.21174L19.1446 10.5045C18.7916 10.5911 18.4249 10.6067 18.0658 10.5503C17.7067 10.4938 17.3625 10.3666 17.0532 10.1759C16.744 9.98527 16.4759 9.73504 16.2647 9.43984C16.0535 9.14464 15.9033 8.8104 15.823 8.45665C15.4803 6.96385 16.4109 5.46047 17.9026 5.09906L33.5259 1.31021C34.1322 1.06779 34.8043 1.04442 35.4261 1.24414V1.24439Z';

export function AdSystemIcon({size = 16, title}: AdSystemIconProps) {
  // Один документ может нести несколько таких знаков, а id внутри svg
  // глобальны: без уникального префикса второй знак заберёт градиенты
  // первого.
  const uid = useId().replace(/:/g, '');
  const clip = `${uid}-clip`;
  const disc = `${uid}-disc`;
  const arrow = `${uid}-arrow`;

  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      // Знак не подчиняется цвету текста — на то он и марка.
      style={{flex: 'none', display: 'block'}}
    >
      {title && <title>{title}</title>}
      <g transform={`translate(${OFFSET_X} ${OFFSET_Y})`}>
        <g clipPath={`url(#${clip})`}>
          <path d={DISC} fill={`url(#${disc})`} />
          <path fillRule='evenodd' clipRule='evenodd' d={ARROW} fill={`url(#${arrow})`} />
        </g>
      </g>
      <defs>
        <clipPath id={clip}>
          <ellipse cx='22.35' cy='10.3277' rx='10.35' ry='10.3277' />
        </clipPath>
        <linearGradient
          id={disc}
          x1='14.7055'
          y1='3.46199'
          x2='29.5525'
          y2='17.9293'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#0064D2' />
          <stop offset='1' stopColor='#3700F0' />
        </linearGradient>
        <linearGradient
          id={arrow}
          x1='4.47211'
          y1='20.3624'
          x2='46.1964'
          y2='2.22311'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#FFF01E' />
          <stop offset='1' stopColor='#FFD21E' />
        </linearGradient>
      </defs>
    </svg>
  );
}
